import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { useCardController, useDialog } from "@datanac/datanac-mui-toolkit";
import { HistoricalYieldTrendChart } from "@datanac/datanac-visualization-toolkit/dist/Components/rma-adm/HistoricalYieldTrendChart";
import { ProducerFarmCropWithHistoryChart } from "@datanac/datanac-visualization-toolkit/dist/Components/rma-aip-pass/ProducerFarmCropWithHistoryChart";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import { Box, Divider, Grid, MenuItem, Select, Stack, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { ApiHelper, UsersApiHelper, UsersApiHelperForMember } from "api/ApiHelper";
import { AppContext } from "App";
import { FarmVaultIcon } from "components/Icons/Icons";
import { WORKSPACE_VIEW_CARD, WORKSPACE_VIEW_TABLE } from "components/Workspaces/helpers";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { formatPracticeName } from "utility/formatters";

import ProducerFarmCropCard from "./components/ProducerFarmCropCard";
import ProducerFarmCropDataTable from "./components/ProducerFarmCropDataTable";
import ProducerFarmCropWithHistoryDataGrid, {
  COLUMNS_PRODUCERFARMCROPWITHHISTORY,
} from "./components/ProducerFarmCropWithHistoryDataGrid";
import { CROP_YEAR_POTENTIAL } from "./helpers";

export default function FarmListWorkspace() {
  const { globalState } = useContext(AppContext);
  const { crop_year, location_state_name, location_county_name } = useParams();
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const { component: confirmDialogComponent } = useDialog();
  const [isLoading, setIsLoading] = useState(true);

  const CARD_GRID_KEY = "farm_list";
  const [workspaceMode, setWorkspaceMode] = useState(null);

  // ---

  const { state: producerFarms, actions: producerFarmActions } = useCardController();

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const loadProducerFarms = async () => {
    try {
      const producerFilter = {
        producer_token: globalState.producer_token,
        location_state_name,
        location_county_name,
        ...(crop_year !== CROP_YEAR_POTENTIAL && { crop_year }),
        is_active: true,
      };
      const farms = await UsersApiHelper.selectProducerFarms(producerFilter, globalState);
      if (farms?.length) {
        producerFarmActions.replace(farms);
      } else {
        producerFarmActions.replace([]);
      }

      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      producerFarmActions.replace([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (globalState?.producer_token) {
      loadProducerFarms();
    } else {
      setIsLoading(false);
    }
  }, [globalState?.producer_token]);

  // ---

  const { state: producerFarmFieldCrops, actions: producerFarmFieldCropActions } = useCardController();
  const loadProducerFarmFieldCrop = async () => {
    try {
      const producerFilter = {
        producer_token: globalState.producer_token,
        location_state_name,
        location_county_name,
        ...(crop_year !== CROP_YEAR_POTENTIAL && { crop_year }),
        is_active: true,
      };
      const farms = await UsersApiHelper.selectProducerFarmFieldCrops(producerFilter, globalState);
      producerFarmFieldCropActions.replace(farms);

      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      producerFarmFieldCropActions.replace([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (globalState?.producer_token) {
      loadProducerFarmFieldCrop();
    } else {
      setIsLoading(false);
    }
  }, [globalState?.producer_token]);

  // ---

  const { state: producerFarm_union_Crops, actions: producerFarm_union_CropActions } = useCardController();

  useEffect(() => {
    if (producerFarms?.length && !producerFarmFieldCrops?.length) {
      producerFarm_union_CropActions.replace(producerFarms);
    } else if (producerFarms?.length && (producerFarmFieldCrops?.length || producerFarmFieldCrops?.length)) {
      const loadUnionProducerFarmCrops = async () => {
        // Union:
        const _producerFarmsUnion = [...producerFarms];
        _producerFarmsUnion.forEach((currentProducerFarm) => {
          currentProducerFarm.ProducerFarmCrops = producerFarmFieldCrops.filter(
            (crop) => crop.producer_farm_id === currentProducerFarm.id
          );

          currentProducerFarm.CropProfiles = _.uniqBy(currentProducerFarm?.ProducerFarmCrops, (pfc) =>
            JSON.stringify({
              commodity_name: pfc.commodity_name,
              type_name: pfc.type_name,
              practice_name: pfc.practice_name,
            })
          )?.sort(
            (a, b) =>
              a.commodity_name?.localeCompare(b.commodity_name) ||
              a.type_name?.localeCompare(b.type_name) ||
              a.practice_name?.localeCompare(b.typpractice_namee_name)
          );
          currentProducerFarm.CropProfiles_Sort = currentProducerFarm.CropProfiles?.reduce(
            (accumulator, currentValue) =>
              (accumulator?.length ? ", " : "") +
              currentValue?.commodity_name +
              "-" +
              formatPracticeName(currentValue?.practice_name),
            ""
          );
        });

        producerFarm_union_CropActions.replace(_producerFarmsUnion);
      };
      loadUnionProducerFarmCrops();
    } else {
      producerFarm_union_CropActions.replace([]);
      setIsLoading(false);
    }
  }, [globalState?.producer_token, producerFarms, producerFarmFieldCrops]);

  // ---

  const handleProducerFarmCropCardOnChange = async (_producerFarmCrop) => {
    UsersApiHelper.updateProducerFarm(_producerFarmCrop, globalState).then(() => {
      loadProducerFarms();
    });
  };

  const handleAddNewClick = () => navigate(`/vault/${crop_year}/${location_state_name}/${location_county_name}/0`);

  // --- Farm History (all farms in county)

  const { state: history, actions: historyActions } = useCardController([]);
  const loadProducerFarmCropWithHistory = async () => {
    try {
      // FieldCropHistory SP uses state_name/county_name; AIP-PASS fallback uses location_*
      const historyFilter = {
        agent_token: globalState.agent_token,
        producer_token: globalState.producer_token,
        state_name: location_state_name,
        county_name: location_county_name,
        location_state_name,
        location_county_name,
        yield_type_code: "A",
      };

      let historyRaw = await UsersApiHelperForMember.callPost(
        "ProducerFarmFieldCropHistory/Search",
        historyFilter,
        globalState
      );
      if (!historyRaw?.length) {
        historyRaw = await ApiHelper.callPost("ProducerFarmCropWithHistory/Search", historyFilter, globalState);
      }
      const historyMapped = historyRaw.map((h, index) => ({
        id: index,
        ...h,
        location_state_name: h.location_state_name ?? h.state_name,
        location_county_name: h.location_county_name ?? h.county_name,
      }));

      historyMapped.forEach((h, index) => {
        h.id = index;
        h.yield_commodity_year = h.yield_commodity_year ?? h.yield_year;
        h.yield_acreage = h.yield_acreage ?? h.year_reported_acreage;
        h.unique_key = JSON.stringify(COLUMNS_PRODUCERFARMCROPWITHHISTORY.map((col) => h[col?.field]));
      });
      const _historyUnique = _.uniqBy(historyMapped, "unique_key");
      historyActions.replace(_historyUnique);
    } catch (err) {
      historyActions.replace([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (globalState?.producer_token && location_state_name && location_county_name) {
      loadProducerFarmCropWithHistory();
    }
  }, [location_state_name, location_county_name, globalState?.producer_token]);

  const [historyCropProfiles, setHistoryCropProfiles] = useState(null);
  const [historySelectedCropProfile, setHistorySelectedCropProfiles] = useState(null);
  const [selectedCountyPracticeHistory, setSelectedCountyPracticeHistory] = useState(null);

  const loadHistoryCropProfiles = async () => {
    const _historyCropProfiles = _.uniqBy(history, (pfc) =>
      JSON.stringify({
        commodity_name: pfc.commodity_name,
        type_name: pfc.type_name,
        practice_name: pfc.practice_name,
      })
    )
      ?.map((x) => ({
        reinsurance_year: getCurrentReinsuranceYear(),
        commodity_name: x.commodity_name,
        type_name: x.type_name,
        practice_name: x.practice_name,
        state_name: x.location_state_name,
        county_name: x.location_county_name,
      }))
      ?.sort(
        (a, b) =>
          a.commodity_name?.localeCompare(b.commodity_name) ||
          a.type_name?.localeCompare(b.type_name) ||
          a.practice_name?.localeCompare(b.practice_name)
      );
    setHistoryCropProfiles(_historyCropProfiles);
  };

  useEffect(() => {
    if (history?.length) {
      loadHistoryCropProfiles();
    }
  }, [history?.length]);

  useEffect(() => {
    if (historyCropProfiles?.length) {
      setHistorySelectedCropProfiles(historyCropProfiles[0]);
    }
  }, [historyCropProfiles?.length]);

  const handleChangeHistorySelectedCropProfileSelect = (event) => {
    setHistorySelectedCropProfiles(JSON.parse(event.target.value));
  };

  const loadSelectedCountyPracticeHistory = async () => {
    const _filteredHistory = history?.filter(
      (h) =>
        (h.location_state_name ?? h.state_name) === location_state_name &&
        (h.location_county_name ?? h.county_name) === location_county_name &&
        h.commodity_name === historySelectedCropProfile.commodity_name &&
        h.type_name === historySelectedCropProfile.type_name &&
        h.practice_name === historySelectedCropProfile.practice_name
    );
    if (_filteredHistory?.length) {
      setSelectedCountyPracticeHistory(_filteredHistory);
    } else {
      setSelectedCountyPracticeHistory(null);
    }
  };

  useEffect(() => {
    if (history?.length && historySelectedCropProfile) {
      loadSelectedCountyPracticeHistory();
    }
  }, [history?.length, historySelectedCropProfile]);

  return (
    <>
      <WorkspaceHeader
        title={
          location_state_name && location_county_name ? location_county_name + ", " + location_state_name : "Farm Vault"
        }
        category={{
          title: "Farm Vault",
          icon: <FarmVaultIcon />,
        }}
        breadCrumbs={[
          {
            title:
              (crop_year !== CROP_YEAR_POTENTIAL ? "Planted Acres" : "Farm Potential") +
              ((crop_year !== CROP_YEAR_POTENTIAL && " - " + crop_year) || ""),
            path: "/vault" + (crop_year === CROP_YEAR_POTENTIAL ? "/potential" : ""),
          },
        ]}
      />

      <WorkspacePageHeader
        title="Farms"
        viewMode={workspaceMode}
        onViewModeChange={setWorkspaceMode}
        cardGridKey={CARD_GRID_KEY}
        isLoading={isLoading}
      />

      {workspaceMode === WORKSPACE_VIEW_CARD && (
        <Box className="cardContainer">
          {producerFarm_union_Crops.map((currentProducerFarmCrop, index) => (
            <ProducerFarmCropCard
              key={index}
              currentProducerFarmCrop={currentProducerFarmCrop}
              onChange={handleProducerFarmCropCardOnChange}
            />
          ))}
        </Box>
      )}

      {workspaceMode === WORKSPACE_VIEW_TABLE && Boolean(producerFarm_union_Crops?.length) && (
        <ProducerFarmCropDataTable
          producerFarmCrops={producerFarm_union_Crops}
          isLoading={isLoading}
          onChange={handleProducerFarmCropCardOnChange}
        />
      )}

      {Boolean(history?.length) && (
        <>
          <Divider />
          <WorkspacePageHeader
            title="Farm History"
            additionalControls={
              historyCropProfiles?.length && historySelectedCropProfile ? (
                <Select
                  labelId="historySelectedCropProfileLabel"
                  value={JSON.stringify(historySelectedCropProfile)}
                  name={"historySelectedCropProfile"}
                  onChange={handleChangeHistorySelectedCropProfileSelect}
                  label={"Crop"}
                >
                  {historyCropProfiles?.map((currentItem, index) => {
                    return (
                      <MenuItem key={index} value={JSON.stringify(currentItem)}>
                        {currentItem.commodity_name + " "}/{" "}
                        {currentItem.type_name === "No Type Specified" ? "" : currentItem.type_name + " / "}
                        {currentItem.practice_name}
                      </MenuItem>
                    );
                  })}
                </Select>
              ) : null
            }
          />
          <ProducerFarmCropWithHistoryDataGrid producerFarmCropWithHistory={selectedCountyPracticeHistory || []} />
        </>
      )}

      {historySelectedCropProfile && Boolean(selectedCountyPracticeHistory?.length) && (
        <>
          <Typography variant="h1">Farm / County Comparison</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <HistoricalYieldTrendChart
                key={`county-yield-${historySelectedCropProfile.commodity_name}-${historySelectedCropProfile.type_name}-${historySelectedCropProfile.practice_name}`}
                countyPractice={{
                  reinsurance_year: crop_year !== CROP_YEAR_POTENTIAL ? crop_year : getCurrentReinsuranceYear(),
                  commodity_name: historySelectedCropProfile?.commodity_name,
                  type_name: historySelectedCropProfile?.type_name,
                  practice_name: historySelectedCropProfile?.practice_name,
                  state_name: location_state_name,
                  county_name: location_county_name,
                }}
                yield_year_minimum={null}
                yield_year_maximum={null}
                api={ApiHelper}
                size={null}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProducerFarmCropWithHistoryChart
                key={`farm-yield-${historySelectedCropProfile.commodity_name}-${historySelectedCropProfile.type_name}-${historySelectedCropProfile.practice_name}`}
                countyPractice={{
                  reinsurance_year: crop_year !== CROP_YEAR_POTENTIAL ? crop_year : getCurrentReinsuranceYear(),
                  commodity_name: historySelectedCropProfile?.commodity_name,
                  type_name: historySelectedCropProfile?.type_name,
                  practice_name: historySelectedCropProfile?.practice_name,
                  state_name: location_state_name,
                  county_name: location_county_name,
                }}
                producerFarmCropWithHistory={selectedCountyPracticeHistory}
                yield_year_minimum={null}
                yield_year_maximum={null}
                api={ApiHelper}
                size={null}
              />
            </Grid>
          </Grid>
        </>
      )}

      <WorkspaceActionBar
        content={
          <>
            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={handleAddNewClick} disabled={crop_year === CROP_YEAR_POTENTIAL}>
                <AddCircleOutline sx={{ mr: 1 }} />
                New Farm
              </Button>
            </Stack>
          </>
        }
      />

      {confirmDialogComponent}
    </>
  );
}
